import { defineStore } from "pinia";
import { Library, emptyLibrary } from "~/entities/Library";
import { LibraryContent, emptyLibraryContent, addLibraryContentSortId } from "~/entities/LibraryContent";
import type { ILibraryRepository } from "~/interfaces/ILibraryRepository";
import { LibraryRepository } from "~/Repositories/LibraryRepository";
import { useContentsStore } from "~/stores/ContentsStore";
import { useUserStore } from "~/stores/UserStore";

/**
 * セレクションデータを扱うクラス
 */
export const useLibraryStore = defineStore("LibraryStore", () => {
  const libraries = ref<Library[]>([]);
  const selectedPlanId = ref<string>("");
  const selectedLibrary = ref<Library>(emptyLibrary());
  const editingLibrary = ref<Library>(emptyLibrary());
  const editingContents = ref<LibraryContent[]>([]);
  const editingContentsBackup = ref<LibraryContent[]>([]);
  // 編集中のフォルダ
  const editingFolder = ref<LibraryContent>();

  const loading = ref(false);

  const repository: ILibraryRepository = new LibraryRepository();

  const contentsStore = useContentsStore();
  const userStore = useUserStore();

  const getLibraries = async (planId: string) => {
    loading.value = true;
    libraries.value = [];
    try {
      const response = await repository.getLibraries(planId);
      libraries.value = response.libraries;
      loading.value = false;
    } catch (error: any) {
      loading.value = false;
    }
  };

  // contentsとdirectoryに分ける
  const divideContents = (libraryContents: LibraryContent[]) => {
    const directories: LibraryContent[] = [];
    const contents: LibraryContent[] = [];

    let index = 0;
    // contentsとdirectoryに分ける
    libraryContents.forEach(content => {
      content.displayOrder = index;
      index++;
      if (content.isDirectory) {
        content.directoryContents.forEach(c => {
          c.displayOrder = index;
          index++;
        });
        directories.push(content);
      } else {
        contents.push(content);
      }
    });
    return { contents, directories };
  };

  // contentsリストを整理する
  const prepareContentsList = (contentsList: LibraryContent[]) => {
    if (!contentsList) {
      return;
    }
    // directoryにはIDがないので、IDを付与する
    let directoryId = 0;
    contentsList.forEach(content => {
      if (content.isDirectory) {
        content.id = directoryId;
        content.tempChildren = [];
        directoryId++;
      } else {
        content.tempChildren = [];
        content.directoryContents = [];
      }
      addLibraryContentSortId(content);
    });
  };

  // contentsリストをマージする
  // params: contentsList1: マージ先のリスト, contentsList2: マージ元のリスト、excludeEmptyDirectory: ディレクトリが空の場合は削除するかどうか
  const mergeContentsList = (
    contents1: LibraryContent[],
    contents2: LibraryContent[],
    excludeEmptyDirectory: boolean = false,
  ) => {
    let contentsList1 = contents1 ? [...contents1] : [];
    let contentsList2 = contents2 ? [...contents2] : [];
    if (excludeEmptyDirectory) {
      // 空のディレクトリは削除する
      contentsList1 = contentsList1.filter(content => {
        if (content.isDirectory && (content.directoryContents ? content.directoryContents.length : 0) === 0) {
          return false;
        }
        return true;
      });
      contentsList2 = contentsList2.filter(content => {
        if (content.isDirectory && (content.directoryContents ? content.directoryContents.length : 0) === 0) {
          return false;
        }
        return true;
      });
    }

    // 同じIDのコンテンツがあったらdirectoryContentsに追加する
    // 同じIDのコンテンツがなかったらcontentsに追加する
    contentsList2.forEach(content2 => {
      const content1 = contentsList1.find(content => content.id === content2.id);
      if (content1) {
        if (content1.isDirectory) {
          const directoryContent = content1.directoryContents.find(c => c.id === content2.id);
          if (!directoryContent) {
            // content1.directoryContentsにcontent2.directoryContentsを追加する
            content2.directoryContents.forEach(directoryContent => {
              content1.directoryContents.push(directoryContent);
            });
          }
        } else {
          contentsList1.push(content2);
        }
      } else {
        contentsList1.push(content2);
      }
    });
    // contentsList1をdisplayOrderでソートする
    contentsList1.sort((a, b) => {
      if (a.displayOrder === undefined || b.displayOrder === undefined) {
        return 0;
      }
      return a.displayOrder - b.displayOrder;
    });
    return contentsList1;
  };

  const getContentsProgress = async (libraryContents: LibraryContent[]) => {
    for (const content of libraryContents) {
      if (content.isDirectory) {
        // ディレクトリの中のコンテンツのComplete状態を取得する
        if (content.directoryContents && content.directoryContents.length > 0) {
          await getContentsProgress(content.directoryContents);
        }
      } else {
        const contentInfo = await contentsStore.getUserContentsInfo(String(userStore.user?.userId), String(content.id));
        if (content.contentsType === "article") {
          content.progress = content.isComplete ? 100 : 0;
        } else if (content.contentsType === "document") {
          content.progress = Math.floor((contentInfo?.maxPage / content.documentTotalPage) * 100);
        } else if (["movie", "archive_movie"].includes(content.contentsType)) {
          if (content.isComplete) {
            content.progress = 100;
          } else {
            content.progress = Math.floor((contentInfo?.maxTime / content.moviePlayTimeSec) * 100);
          }
        }
        if (content.progress > 100) content.progress = 100;
        if (content.progress < 0) content.progress = 0;
        if (content.progress === undefined) content.progress = 0;
        if (Number.isNaN(content.progress)) content.progress = 0;
      }
    }
  };

  const getDirectoryProgress = (libraryContents: LibraryContent[]) => {
    for (const content of libraryContents) {
      if (content.isDirectory) {
        // ディレクトリの中のコンテンツのComplete状態を取得する
        if (content.directoryContents && content.directoryContents.length > 0) {
          let progress = 0;
          let isComplete = true;
          content.directoryContents.forEach(c => {
            if (c.isComplete) {
              progress += 100;
            } else {
              isComplete = false;
              progress += c.progress;
            }
            c.directoryContents = [];
            c.tempChildren = [];
          });
          progress = Math.floor(progress / content.directoryContents.length);
          content.progress = progress;
          content.isComplete = isComplete;
        } else {
          content.progress = 0;
          content.isComplete = false;
        }
      }
    }
  };

  const getLibrary = async (libraryId: string, page: number, pageSize: number) => {
    const response = await repository.getLibrary(libraryId, page, pageSize);
    prepareContentsList(response.contents);
    return response;
  };

  const createLibrary = async (planId: string, library: Library, libraryContents: LibraryContent[]) => {
    const { contents, directories } = divideContents(libraryContents);
    return await repository.createLibrary(planId, library, contents, directories);
  };

  const updateLibrary = async (planId: string, library: Library, libraryContents: LibraryContent[]) => {
    const { contents, directories } = divideContents(libraryContents);
    return await repository.updateLibrary(planId, library, contents, directories);
  };

  const deleteLibrary = async (planId: string, libraryId: string) => {
    return await repository.deleteLibrary(planId, libraryId);
  };

  const browseLibrary = async (libraryId: string) => {
    return await repository.browseLibrary(libraryId);
  };

  const updateDisplaySort = async (planId: string, libraryIds: string[]) => {
    return await repository.updateDisplaySort(planId, libraryIds);
  };

  const startEdit = (library?: Library, contents?: LibraryContent[]) => {
    editingLibrary.value = library ?? emptyLibrary();
    editingContents.value = contents ?? [];
    editingFolder.value = undefined;
  };

  const startEditFolder = (content?: LibraryContent) => {
    if (content) {
      editingFolder.value = content;
      editingFolder.value.isEdit = true;
    } else {
      editingFolder.value = emptyLibraryContent();
      editingFolder.value.isDirectory = true;
      editingFolder.value.isEdit = false;
      //  editingContents.valueの中にあるフォルダのIDの最大値を取得する
      const maxId = editingContents.value.reduce((max, content) => {
        if (content.isDirectory && content.id > max) {
          return content.id;
        }
        return max;
      }, 0);

      editingFolder.value.id = maxId + 1;
      addLibraryContentSortId(editingFolder.value);
    }
  };

  const endEditFolder = () => {
    editingFolder.value = undefined;
  };

  const backupEditingContents = () => {
    editingContentsBackup.value = JSON.parse(JSON.stringify(editingContents.value));
  };
  const restoreEditingContents = () => {
    editingContents.value = [...editingContentsBackup.value];
    editingContentsBackup.value = [];
  };

  return {
    loading,
    libraries,
    selectedPlanId,
    selectedLibrary,
    editingLibrary,
    editingContents,
    editingFolder,
    mergeContentsList,
    getContentsProgress,
    getDirectoryProgress,
    getLibraries,
    getLibrary,
    createLibrary,
    updateLibrary,
    updateDisplaySort,
    deleteLibrary,
    browseLibrary,
    startEdit,
    startEditFolder,
    endEditFolder,
    backupEditingContents,
    restoreEditingContents,
  };
});
